import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { compareAsc } from 'date-fns';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { NBA_SCORES_GAMEID } from '@sorare/core/src/constants/__generated__/routes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { useAnySportEvents } from '@sorare/marketplace/src/shared/events/lib';

import { PlayerGameScoreDrawer } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDrawer';
import { MatchCenterPage } from '@sorare/gameplay/src/pages/MatchCenterPage';

import type {
  NBAScoresQuery,
  NBAScoresQueryVariables,
} from './__generated__/page.graphql';

const NBA_SCORES_QUERY = gql`
  query NBAScoresQuery {
    so5 {
      so5Fixtures(
        eventType: CLASSIC
        aasmStates: ["started", "closed", "opened"]
        sport: NBA
        first: 3
      ) {
        nodes {
          slug
          sport
          type
          ...MatchCenterPage_so5Fixture
        }
      }
    }
  }
  ${MatchCenterPage.fragments.so5Fixture}
` as TypedDocumentNode<NBAScoresQuery, NBAScoresQueryVariables>;

export const loader = queryLoader(NBA_SCORES_QUERY);

const ScoresPage = () => {
  const { data } = useQueryLoader<NBAScoresQuery>();

  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();
  const navigate = useNavigate();
  const track = useAnySportEvents();
  const isLaptopAndAbove = useIsLaptop();

  const { so5Fixtures } = data.so5 || {};

  const sortedFixtures = [...(so5Fixtures?.nodes || [])].sort((a, b) =>
    compareAsc(a.startDate, b.startDate)
  );

  return (
    <>
      <PageHeader text={scoutingCenter.scout} disableBackBrowser />
      <MatchCenterPage
        withAllGames
        withFixtureHeader={false}
        so5Fixtures={sortedFixtures}
        onClickGame={id => {
          track('Open Match View', {
            gameId: id,
            deviceType: `web_${isLaptopAndAbove ? 'desktop' : 'mobile'}`,
            interactionContext: 'match_center',
          });
          navigate(generatePath(NBA_SCORES_GAMEID, { gameId: id }));
        }}
        onClickPlayerGameScore={(scoreId, playerSlug) => {
          track('Open Match Center Appearance Details', {
            playerSlug,
          });
          setPlayerGameScoreId(scoreId);
        }}
      />
      <>
        {!!playerGameScoreId && (
          <PlayerGameScoreDrawer
            open
            onClose={() => setPlayerGameScoreId(undefined)}
            playerGameScoreId={playerGameScoreId}
            so5FixtureSlug={sortedFixtures[0].slug}
            withListingsFooter
          />
        )}
      </>
    </>
  );
};

export default ScoresPage;
